import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';

import { toMachineNumber } from '@shared/format';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';

import { Field, FIELD_CONTROL_CLASS } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import type { Plan } from '../_shared/types';
import { copy } from './copy';
import { planFormSchema, type PlanFormValues } from './forms.schema';

/**
 * One dialog, two modes.
 *
 * The route map carries **one** plan surface — there is no plan-edit route — so
 * editing happens here or it does not happen at all, and «management» is not
 * satisfied by a list an operator can only delete from. Create is the same
 * dialog with the defaults and a different heading.
 *
 * A native `<dialog>` opened with `showModal()` is the modal: the focus trap,
 * the backdrop, `Esc`, and focus return to the trigger are the browser's.
 *
 * **Both numeric fields are `dir="ltr"` and carry ASCII digits.** What the
 * operator types is what the wire receives; the Arabic-Indic rendering happens
 * in the table after the save, at the render site, and the two layers never
 * convert into each other. The schema still folds an Arabic-Indic keystroke
 * before validating, so a keyboard is never a rejection.
 *
 * **The scope is stated at the foot and is not a field.** Every plan opens the
 * catalogue in full for its period; that is a platform setting, and a control
 * with one possible value reads as a question whose answer never varies.
 */
export function PlanDialog({
  open,
  plan,
  currency,
  busy,
  formError,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  /** `null` in create mode. */
  plan: Plan | null;
  /**
   * The currency word to name in the price hint, read from a plan row rather
   * than held as a literal anywhere in this repo. `null` before any plan exists,
   * where no row has yet been written from the setting — the hint then states
   * the shape of the number and stays silent about the unit rather than naming
   * one this console chose.
   */
  currency: string | null;
  busy: boolean;
  /** A form-level refusal from the server — rendered above the fields. */
  formError: string | null;
  onSubmit: (values: PlanFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const alertId = useId();

  const form = useForm<PlanFormValues>({
    resolver: zodResolver(planFormSchema),
    defaultValues: { name: '', price: '', periodMonths: '' },
  });

  const { register, handleSubmit, reset, formState } = form;

  // The dialog is one element serving the whole table, so its values are seeded
  // from the row that opened it every time it opens — never left holding the
  // previous subject's numbers.
  useEffect(() => {
    if (!open) return;

    reset({
      name: plan?.name ?? '',
      price: plan ? toMachineNumber(plan.price) : '',
      periodMonths: plan ? toMachineNumber(plan.period_months) : '1',
    });
  }, [open, plan, reset]);

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'save') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of saving on the way out.
          if (isCancelledSubmit(event)) return;
          // The commit is validated and dispatched from here; the dialog is
          // closed by the caller once the write lands, so a refusal keeps the
          // operator's values on screen instead of discarding them.
          event.preventDefault();
          void handleSubmit(onSubmit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {plan ? copy.dialog.editTitle : copy.dialog.createTitle}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.dialog.context}</p>
        </div>

        {formError ? (
          <p id={alertId} role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <Field label={copy.dialog.nameLabel} error={formState.errors.name?.message}>
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="text"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={FIELD_CONTROL_CLASS}
              {...register('name')}
            />
          )}
        </Field>

        <Field
          label={copy.dialog.priceLabel}
          hint={currency ? copy.dialog.priceHint(currency) : copy.dialog.priceHintNoCurrency}
          error={formState.errors.price?.message}
        >
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="text"
              inputMode="numeric"
              dir="ltr"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={`mono ${FIELD_CONTROL_CLASS}`}
              {...register('price')}
            />
          )}
        </Field>

        <Field
          label={copy.dialog.periodLabel}
          hint={copy.dialog.periodHint}
          error={formState.errors.periodMonths?.message}
        >
          {(id, describedBy, invalid) => (
            <input
              id={id}
              type="text"
              inputMode="numeric"
              dir="ltr"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required
              className={`mono ${FIELD_CONTROL_CLASS}`}
              {...register('periodMonths')}
            />
          )}
        </Field>

        <p className="mb-0 max-w-none text-xs text-ink-muted">
          {copy.dialog.scopeLead} <b className="font-semibold text-ink">{copy.dialog.scopeStrong}</b>{' '}
          {copy.dialog.scopeRest}
        </p>

        <div className="flex flex-wrap gap-2">
          {/*
           * Both buttons in a `method="dialog"` form are submits, and a submit
           * runs constraint validation — without `formNoValidate` the browser
           * blocks the **exit** and demands a value from an operator who has
           * just decided not to supply one.
           */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="save"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {sharedCopy.action.save}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
